import React from "react";
import { useLocation } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../services/firebase";
import { books_getBundle, books_getInventory } from "../../lib/firestoreAdapters";
import { refreshStudentsFromFirestore } from "../../services/studentsService";

export default function StudentOrderPrint() {
  const qs = new URLSearchParams(useLocation().search);
  const campus = qs.get("campus") || "gapan";
  const schoolYear = qs.get("sy") || "";
  const sidParam = qs.get("sid") || "";
  const sidsParam = qs.get("sids") || "";
  const grade = qs.get("grade") || "";
  const date = qs.get("date") || new Date().toISOString().slice(0,10);
  const orNo = qs.get("or") || qs.get("orNo") || "";
  const sidList = (sidsParam ? sidsParam.split(",") : []).map(s => s.trim()).filter(Boolean);
  if (!sidList.length && sidParam) sidList.push(sidParam);
  const [students, setStudents] = React.useState([]);
  const [bundle, setBundle] = React.useState(null);
  const [inv, setInv] = React.useState([]);
  const [ownedMap, setOwnedMap] = React.useState(new Map()); // key sid -> Set(keys)

  React.useEffect(() => {
    (async () => {
      try {
        const list = await refreshStudentsFromFirestore({ campus });
        setStudents(list || []);
      } catch { setStudents([]); }
      try {
        const b = await books_getBundle(campus, schoolYear, grade);
        setBundle(b || null);
      } catch { setBundle(null); }
      try {
        const invData = await books_getInventory(campus, schoolYear);
        setInv(invData);
      } catch { setInv([]); }
      try {
        const snap = await getDocs(query(
          collection(db, "books_student_items"),
          where("campus","==",campus),
          where("schoolYear","==",schoolYear)
        ));
        const tmp = new Map();
        snap.docs.forEach(d => {
          const r = d.data();
          const sid = String(r.studentId || "");
          if (!sid) return;
          const key = (r.isbn && r.isbn.length) ? `isbn:${r.isbn}` : `title:${r.title}`;
          const set = tmp.get(sid) || new Set();
          set.add(key);
          tmp.set(sid, set);
        });
        setOwnedMap(tmp);
      } catch { setOwnedMap(new Map()); }
      setTimeout(() => window.print(), 400);
    })();
  }, [campus, schoolYear, grade, sidsParam, sidParam]);

  const requiredItems = React.useMemo(() => {
    const arr = Array.isArray(bundle?.items) ? bundle.items : [];
    const getPrice = (it) => {
      const m = inv.find(b => b.id === it.id) || inv.find(b => (b.isbn||"") === (it.isbn||"") || b.title === it.title);
      return Number(m?.price || it.price || 0);
    };
    return arr.map(it => ({ title: it.title, isbn: it.isbn || "", price: getPrice(it) }));
  }, [bundle, inv]);
  const studentRecords = sidList.map(id => {
    const s = students.find(x => String(x.id) === String(id)) || {};
    const owned = ownedMap.get(String(id)) || new Set();
    const items = requiredItems.map(it => ({
      ...it,
      owned: owned.has(it.isbn ? `isbn:${it.isbn}` : `title:${it.title}`)
    }));
    const total = items.reduce((sum, it) => sum + Number(it.price || 0), 0);
    const section = s.section || (Array.isArray(s.sections) ? s.sections[0] : "");
    return { id, name: s.fullname || s.name || id, section, items, total };
  });

  return (
    <div className="container py-4" style={{ maxWidth: 960 }}>
      <div className="d-flex justify-content-between align-items-center mb-3 print-hide">
        <button className="btn btn-outline-secondary" onClick={() => window.close && window.close()}>Close</button>
        <button className="btn btn-primary" onClick={() => window.print()}>Print</button>
      </div>
      <div className="row g-3">
        {studentRecords.map((rec, idx) => (
          <div key={rec.id} className="col-12">
            <div className="sheet">
              <div className="text-center">
                <h4 className="fw-bold m-0">GRADE {String(grade).replace(/[^0-9]/g,"") || grade} BOOKS</h4>
                <div>ACADEMIC YEAR {schoolYear}</div>
              </div>
              <div className="d-flex justify-content-between mt-2 mb-1">
                <div><strong>STUDENT NAME:</strong> {rec.name}</div>
                <div><strong>DATE:</strong> {date}</div>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered mb-2">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 100 }}>ORDER (✓)</th>
                      <th style={{ width: 120 }}>PRICE</th>
                      <th>BOOK TITLE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rec.items.length === 0 ? (
                      <tr><td colSpan="3" className="text-center text-muted">No bundle titles for this grade.</td></tr>
                    ) : rec.items.map((it, i) => (
                      <tr key={i} style={{ height: 36 }}>
                        <td className="text-center">{it.owned ? "✓" : ""}</td>
                        <td>₱{Number(it.price || 0).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                        <td>{it.title}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold">
                      <td className="text-end">TOTAL</td>
                      <td colSpan="2">₱{Number(rec.total || 0).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="row">
                <div className="col-6"><strong>ASSESSED BY:</strong> __________________________</div>
                <div className="col-6"><strong>RECEIVED BY:</strong> __________________________</div>
              </div>
              <div className="mt-1"><strong>OFFICIAL RECEIPT NO.:</strong> {orNo || "__________________"}</div>
            </div>
            {/* page break after each 3 sheets */}
            {(idx % 3 === 2) && <div className="page-break"></div>}
          </div>
        ))}
      </div>
      <style>{`
        @page { size: 8.5in 13in; margin: 0.5in; }
        .print-hide{ display:none !important }
        @media print{
          .sheet { break-inside: avoid; page-break-inside: avoid; }
          .page-break { page-break-after: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
